import { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useSpace } from '@/hooks/useSpace'
import { useNavigation } from '@/hooks/useNavigation'
import { useModal } from '@/contexts/modalContext'
import useGenericStore from '@/hooks/useGenericStore'
import storage from '@/utils/storage'
import getBackendLanguageCode from '@/utils/language'

import type { InitChatParams, LastChatInfo, MMAAssistant, MMAChatMessage, MetadataLine } from './types/MMA'
import { ShiftbookDialogStep } from '@/ui/advancedAssistants/MMA/components/Shiftbook/ShiftbookDialog/types'
import buildAdditionalParams from './utils/buildAdditionalParams'
import type { AssistantConfig } from '@/shared/types/space'
import type { RootState } from '@/utils/store/store'
import type { FilterContentSelection, SelectOption } from './types/Filter'

export function useMMAState() {
    // State
    const [parsedConfig, setParsedConfig] = useState<Partial<AssistantConfig> | undefined>(undefined)
    const [shiftbookState, setShiftbookState] = useState<ShiftbookDialogStep | undefined>(undefined)
    const [currentFilters, setCurrentFilters] = useState<FilterContentSelection | undefined>(undefined)
    const [persistedData, setPersistedData] = useState<LastChatInfo | undefined>(undefined)

    // Contexts/hooks
    const { information, getApiToken, validateTokenAndRehydrate } = useAuthentication()
    const { getActiveSpace, getActiveAssistant, switchAssistant } = useSpace()
    const { navigateTo } = useNavigation()
    const { t, i18n } = useTranslation()
    const { showModal, hideModal, changeConfirmButtonState } = useModal()
    const { setGenericStoreValue, getGenericStoreValue, removeGenericStoreValue } = useGenericStore()

    // Derived values
    const activeSpace = getActiveSpace()
    const activeAssistant = getActiveAssistant()
    const config = activeAssistant?.config
    const handOverFilter = getGenericStoreValue<FilterContentSelection>('handOverFilter')
    const queryParams: InitChatParams | undefined = getGenericStoreValue('queryParams')
    const dataToRehydrate: LastChatInfo | undefined = getGenericStoreValue('dataToRehydrate')
    const savedFilters = getGenericStoreValue<Record<string, FilterContentSelection>>('savedFilters')
    const { lastUpdate } = useSelector((state: RootState) => state.assistants)
    const activeMessageList = activeAssistant?.activeChat?.messageList?.filter(
        (message: MMAChatMessage) =>
            message.role !== 'assistant' || message.messageId || message.message_id
    )

    const parseLineSelection = (lineStr: string | undefined, lineElements: MetadataLine): SelectOption[] => {
        return (lineStr || '')
            .split(',')
            .map((line) => line.trim())
            .filter((line) => !!line && !!lineElements[line])
            .map((line) => ({ value: line, label: lineElements[line].displayName || line }))
    }


    // The sources a troubleshooting assistant starts with: the ones the user chose before that still exist, else all
    // of them. Undefined when the assistant has no sources.
    const getDefaultResolutionSources = (assistant: MMAAssistant | undefined): string[] | undefined => {
        const sourceElements = assistant?.metadata?.filter?.source?.elements
        if (!sourceElements) return undefined

        const validSavedSources = storage.getSelectedResolutionSources().filter((s) => !!sourceElements[s])

        return validSavedSources.length > 0 ? validSavedSources : Object.keys(sourceElements)
    }

    const getFiltersToLoad = (assistant: MMAAssistant | undefined): FilterContentSelection | undefined => {
        if (!assistant) return undefined

        let filtersToLoad = savedFilters?.[assistant?.config.id]

        try {
            const sessionFilters = sessionStorage.getItem(`mmaFilters:${assistant?.config.id}`)
            if (sessionFilters) {
                const parsed = JSON.parse(sessionFilters)
                const lineElements = assistant?.metadata?.filter?.line?.elements || {}
                filtersToLoad = {
                    line: (parsed.line || []).map((lineValue: string) => ({
                        value: lineValue,
                        label: lineElements[lineValue]?.displayName || lineValue,
                    })),
                    source: parsed.source || [],
                }
            }
        } catch {
            // ignore sessionStorage errors, fall back to savedFilters
        }

        const isResolution = assistant?.config.id?.endsWith('-resolution')

        if (!filtersToLoad?.line?.length) {
            // Only the default line of the user preferences is pre-selected. None (or a default this assistant does
            // not know) pre-selects nothing.
            const lineElements = assistant?.metadata?.filter?.line?.elements
            const defaultLine = activeSpace ? storage.getDefaultLine(activeSpace.id) : null

            if (lineElements && defaultLine && lineElements[defaultLine]) {
                filtersToLoad = {
                    source: filtersToLoad?.source ?? [],
                    line: [{ value: defaultLine, label: lineElements[defaultLine].displayName || defaultLine }],
                }
            }
        }

        if (isResolution) {
            const matchingSources = getDefaultResolutionSources(assistant)

            if (matchingSources) {
                filtersToLoad = {
                    line: filtersToLoad?.line ?? [],
                    source: matchingSources
                }

                try {
                    sessionStorage.setItem(
                        `mmaFilters:${assistant.config.id}`,
                        JSON.stringify({ line: filtersToLoad.line?.map((m) => m.value) || [], source: matchingSources })
                    )
                } catch {
                    // ignore
                }
            }
        }

        return filtersToLoad
    }

    const initialiseChatStateForQuery = async (assistant: MMAAssistant, qparams: InitChatParams) => {
        const hasLanguageCode = qparams.languageCode
        const lineElements = assistant?.metadata?.filter?.line?.elements || {}
        const lineSelection = parseLineSelection(qparams.line, lineElements)
        const hasLine = lineSelection.length > 0 ? lineSelection.map((line) => line.value).join(',') : undefined
        const hasMessage = qparams.message

        const currentLanguage = i18n.language

        if (currentLanguage !== hasLanguageCode) await i18n.changeLanguage(hasLanguageCode)

        if (assistant.config.id) {
            // A link without sources (the MTS link) opens a troubleshooting assistant with the same sources as when
            // it is opened without a link: all of them, or the ones the user chose before.
            const linkSources = qparams.source ? qparams.source.split(',') : []
            const defaultSources = assistant.config.id.endsWith('-resolution') ? getDefaultResolutionSources(assistant) : undefined
            const filterSelection = {
                line: lineSelection,
                source: linkSources.length > 0 ? linkSources : defaultSources ?? [],
            }

            setCurrentFilters(filterSelection)
            setGenericStoreValue('savedFilters', { [assistant.config.id]: filterSelection }, false)

            try {
                sessionStorage.removeItem(`mmaFilters:${assistant.config.id}`)
            } catch {
                // ignore
            }
        }

        if (!hasLine && hasMessage) {
            setPersistedData((prev: LastChatInfo | undefined) => ({ ...(prev || {}), message: hasMessage }))

            setGenericStoreValue('mmaParameters', {
                additionalParams: buildAdditionalParams(queryParams),
            }, false)
        }

        removeGenericStoreValue('queryParams', true)
        window.history.replaceState({}, document.title, window.location.pathname)
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)

        if (urlParams.get('handOver') === 'true' && handOverFilter) {
            setCurrentFilters(handOverFilter)
            removeGenericStoreValue('queryParams', true)
            return
        }

        if (activeAssistant && !queryParams) {
            const filtersToLoad = getFiltersToLoad(activeAssistant)
            setCurrentFilters(filtersToLoad)
        }

        if (activeAssistant && activeSpace && queryParams) {
            // The line of the link is matched with the lines of the assistant. A link opened in a new tab finds the
            // assistant active while its metadata is still being fetched: reading it now would match no line and
            // drop the link. It is read when the metadata is there (this effect runs again then).
            if (!activeAssistant.metadata) return

            const hasLanguageCode = queryParams.languageCode
            const hasMessage = queryParams.message

            if (hasLanguageCode && hasMessage) {
                initialiseChatStateForQuery(activeAssistant, queryParams)
            }
        }
        // the filters are loaded when the assistant, its metadata or the hand over filter change: the helpers and the link
        // are read as they are then
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAssistant, activeAssistant?.metadata, activeSpace, handOverFilter])


    useEffect(() => {
        if (dataToRehydrate && activeMessageList &&
            (!dataToRehydrate.activeMessagesCount || activeMessageList.length === dataToRehydrate.activeMessagesCount)) {
            setGenericStoreValue('dataToRehydrate', undefined)

            const currentFilters = dataToRehydrate.currentFilters
            if (currentFilters) {
                setCurrentFilters(currentFilters)
            }

            if (!dataToRehydrate?.message)
                setPersistedData(dataToRehydrate)
        } else if (persistedData)
            setPersistedData(undefined)
        // the data is rehydrated when the assistants are updated (lastUpdate), not when the values it reads change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpdate])

    useEffect(() => {
        const setParams = async () => {
            const availableSourceIds = Object.keys(activeAssistant?.metadata?.filter?.source?.elements || {})
            const selectedSources = currentFilters?.source || []
            // When all available sources are selected, send undefined so the backend applies no filter.
            // This preserves the original "no filter = search all" behaviour and avoids an explicit
            // in() filter that could miss sources added after the metadata was cached.
            // Fall back to sending IDs when metadata has no sources to compare against.
            const allSourcesSelected = availableSourceIds.length > 0 && selectedSources.length >= availableSourceIds.length
            const sourceParam = !allSourcesSelected && selectedSources.length > 0
                ? selectedSources.join(',')
                : undefined

            const params = {
                space: activeSpace?.endpoint,
                assistant: activeAssistant?.config.endpoint,
                userId: information.userId,
                chatId: activeAssistant?.activeChat?.chatId,
                languageCode: getBackendLanguageCode(i18n?.language, activeSpace?.additionalBackendSupportedLanguages),
                line: currentFilters?.line?.map((line) => line.value).join(',') || undefined,
                source: sourceParam,
            }
            setGenericStoreValue('mmaParameters', params)
        }
        setParams()
        // the store setter is not stable: the parameters are written when their inputs change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAssistant, activeSpace, information, i18n, currentFilters])

    useEffect(() => {
        if (config) {
            const updatedConfig = JSON.parse(JSON.stringify(config))
            for (const key of Object.keys(updatedConfig.features.advanced)) {
                if (updatedConfig.features.advanced[key]) {
                    switch (key) {
                        case 'feedback': {
                            const feedback = {
                                component: null, // Feedback component set in MMA.tsx
                                attributes: {
                                    onSendFeedback: null // sendFeedback set in MMA.tsx
                                }
                            }
                            updatedConfig.features.output[key] = feedback
                            break
                        }
                        case 'sendToShiftbook': {
                            const shiftbookObject = {
                                component: null, // SendToShiftbook set in MMA.tsx
                                attributes: {
                                    onClick: null, // showShiftbookModal set in MMA.tsx
                                },
                            }
                            updatedConfig.features.output[key] = shiftbookObject
                            break
                        }
                        case 'handover': {
                            const handoverObject = {
                                component: null, // Handover set in MMA.tsx
                                attributes: {
                                    translate: t,
                                    onClick: null // showHandoverModal set in MMA.tsx
                                },
                            }
                            updatedConfig.features.output[key] = handoverObject
                            break
                        }
                        default:
                        // ??
                    }
                }
            }
            setParsedConfig(updatedConfig)
        }
        // the config is parsed once per assistant config, not when the translation function changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config])

    return {
        parsedConfig,
        setParsedConfig,
        shiftbookState,
        setShiftbookState,
        currentFilters,
        setCurrentFilters,
        persistedData,
        setPersistedData,
        information,
        getApiToken,
        validateTokenAndRehydrate,
        getActiveSpace,
        getActiveAssistant,
        switchAssistant,
        navigateTo,
        t,
        i18n,
        showModal,
        hideModal,
        changeConfirmButtonState,
        setGenericStoreValue,
        getGenericStoreValue,
        removeGenericStoreValue,
        activeSpace,
        activeAssistant,
        config,
        handOverFilter,
        queryParams,
        dataToRehydrate,
        savedFilters,
        lastUpdate,
        activeMessageList,
    }
}
